import React from 'react';
import { Header, Message, Segment } from 'semantic-ui-react';

import Button from '../../../../components/Button';
import { StandaloneChargeActivationStatusDTO } from '../../../../services/standalone-charges';
import BiometryStatusCard from '../BiometryStatusCard';

type Props = {
  compact?: boolean;
  featureEnabled?: boolean;
  activationLoading: boolean;
  activationStatusError: boolean;
  activationStatus: StandaloneChargeActivationStatusDTO | null;
  activationIsPendingKyc: boolean;
  activationIsBiometryRejected?: boolean;
  activationIsSupportRequired?: boolean;
  canRetryBiometry?: boolean;
  activationKycLink: string;
  activationKycPageUrl: string;
  activationKycQrImageUrl: string;
  openKycLink: () => void;
  loadActivationStatus: () => void;
  showActivationAction: boolean;
  activationActionLabel: string;
  hasLocalDraft: boolean;
  openActivationWizard: () => void;
  onRetryBiometry?: () => void;
};

const ActivationGate: React.FC<Props> = ({
  compact = false,
  featureEnabled = true,
  activationLoading,
  activationStatusError,
  activationStatus,
  activationIsPendingKyc,
  activationIsBiometryRejected = false,
  activationIsSupportRequired = false,
  canRetryBiometry = false,
  activationKycLink,
  activationKycPageUrl,
  activationKycQrImageUrl,
  openKycLink,
  loadActivationStatus,
  showActivationAction,
  activationActionLabel,
  hasLocalDraft,
  openActivationWizard,
  onRetryBiometry
}) => {
  if (activationLoading && !compact) {
    return (
      <Segment>
        <Header as="h3" style={{ marginTop: 0 }}>
          Carregando...
        </Header>
      </Segment>
    );
  }

  const wrapperStyle = compact ? { marginBottom: 16 } : undefined;
  const biometryMeta = activationStatus?.activation;

  return (
    <Segment style={wrapperStyle}>
      {!compact && (
        <Header as="h3" style={{ marginTop: 0 }}>
          Ative Cobranças para sua imobiliária
        </Header>
      )}

      {compact && (
        <Header as="h4" style={{ marginTop: 0 }}>
          Ativação de Cobranças da imobiliária
        </Header>
      )}

      {!featureEnabled && (
        <Message warning>
          <Message.Header>Cobranças não habilitadas</Message.Header>
          <p>
            Habilite o envio de cobranças na edição da imobiliária antes de iniciar
            a ativação.
          </p>
        </Message>
      )}

      {featureEnabled && showActivationAction &&
        !activationIsPendingKyc &&
        !activationIsBiometryRejected &&
        !activationIsSupportRequired && (
        <>
          <p style={{ marginBottom: 12 }}>
            {hasLocalDraft
              ? 'Encontramos um cadastro em andamento. Você pode continuar de onde parou.'
              : 'Para emitir cobranças, você precisa concluir o cadastro de ativação.'}
          </p>
          <div style={{ marginBottom: 16 }}>
            <Button
              type="button"
              text={activationActionLabel}
              onClick={openActivationWizard}
            />
          </div>
        </>
      )}

      {featureEnabled && activationStatusError && (
        <Message warning>
          <Message.Header>Não conseguimos verificar o status agora</Message.Header>
          <p>Tente novamente para atualizar o status da ativação.</p>
          <Button type="button" text="Tentar novamente" onClick={loadActivationStatus} />
        </Message>
      )}

      {featureEnabled && activationStatus?.activation?.lastSyncError && (
        <Message negative>
          <Message.Header>Falha no cadastro do recebedor na Pagar.me</Message.Header>
          <p>{activationStatus.activation.lastSyncError}</p>
        </Message>
      )}

      {featureEnabled && activationIsSupportRequired && (
        <BiometryStatusCard variant="support" meta={biometryMeta} />
      )}

      {featureEnabled && activationIsBiometryRejected && (
        <BiometryStatusCard
          variant={canRetryBiometry ? 'rejected' : 'limitReached'}
          meta={biometryMeta}
        >
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {canRetryBiometry && onRetryBiometry && (
              <Button
                type="button"
                text="Tentar biometria novamente"
                onClick={onRetryBiometry}
              />
            )}
            <Button
              type="button"
              text="Atualizar status"
              light
              onClick={loadActivationStatus}
              loading={activationLoading}
              disabled={activationLoading}
            />
          </div>
        </BiometryStatusCard>
      )}

      {featureEnabled && activationIsPendingKyc && (
        <BiometryStatusCard variant="pending" meta={biometryMeta}>
          {activationKycPageUrl && (
            <p style={{ marginBottom: 12 }}>
              <a href={activationKycPageUrl} target="_blank" rel="noopener noreferrer">
                Abrir página de biometria
              </a>
            </p>
          )}
          {activationKycLink && (
            <p style={{ marginBottom: 12 }}>
              <a href={activationKycLink} target="_blank" rel="noopener noreferrer">
                Abrir link direto da Pagar.me
              </a>
            </p>
          )}
          {activationKycQrImageUrl && (
            <div style={{ marginBottom: 12 }}>
              <img
                src={activationKycQrImageUrl}
                alt="QR Code para biometria na Pagar.me"
                style={{ width: 180, height: 180, border: '1px solid #ddd', padding: 4 }}
              />
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button type="button" text="Editar cadastro" onClick={openActivationWizard} />
            <Button type="button" text="Abrir biometria" onClick={openKycLink} />
            <Button
              type="button"
              text="Atualizar status"
              light
              onClick={loadActivationStatus}
              loading={activationLoading}
              disabled={activationLoading}
            />
          </div>
        </BiometryStatusCard>
      )}
    </Segment>
  );
};

export default ActivationGate;
